"use client"
import * as React from "react"
import { Input } from "../../components/ui/input"
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Link, useSearchParams } from "react-router-dom"
import { CountProgress } from "../../components/ui/count-progress"
import { Card, CardHeader, CardContent } from "../../components/ui/card"

import { Button } from "../../components/ui/button"

import { TableRowsSkeleton } from "../../components/ui/card-skeletons"
import AsyncContent from "../../components/ui/AsyncContent"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../components/ui/table"
import { CalendarIcon, Check, RotateCcw, ChevronsUpDown, ArrowRight, Columns3 } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
} from "../ui/dropdown-menu";
import { Calendar } from "../ui/calendar";
import { format } from "date-fns";
import { formatDateInTimeZone, formatTimeInTimeZone, formatDateTimeInTimeZone } from "../../utils/dates";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "../ui/command";
import { Badge } from "../ui/badge";
import PaymentStatus from './PaymentStatus';
import { getPaymentBatchSummary } from '../../utils/paymentBatch';
const currencyFormat = (value) => {
    return new Intl.NumberFormat('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(value);
};

const createColumns = (searchParams, timezone, showOrganisation) => [
    ...(showOrganisation ? [{
        accessorKey: '_organisationName',
        header: 'Organisation',
        cell: info => (
            <Badge variant="secondary" className="whitespace-nowrap">
                {info.row.original._organisationName || '—'}
            </Badge>
        ),
    }] : []),
    { accessorKey: 'createdAt', header: 'Date', cell: info => {
        const value = info.row.original.createdAt;
        return (
            <span title={formatDateTimeInTimeZone(value, timezone)}>
                {formatDateInTimeZone(value, timezone)}
                <span className="text-slate-400"> {formatTimeInTimeZone(value, timezone)}</span>
            </span>
        );
    }},
    { accessorKey: 'type', header: 'Payment Type', cell: info => (
        info.row.original.payments.length > 1 
            ? 'Bulk' 
            : 'Single'
        )
    },
    { accessorKey: 'totalLines', header: 'Total Lines', cell: info => (
            info.row.original.totalLines ?? info.row.original.payments.length
        ), meta: { className: 'text-center' }
    },
    { accessorKey: 'paymentMethod', header: 'Payment Method', cell: info => (
            info.row.original.paymentMethod
        )
    },
    { accessorKey: 'payer', header: 'Payer', cell: info => (
            info.row.original.payer?.name || '—'
        )
    },
    { accessorKey: 'intercompanyTransfer', header: 'Intercompany', cell: info => (
            info.row.original.intercompanyTransfer
                ? <Badge variant="info" className="whitespace-nowrap">Intercompany</Badge>
                : '—'
        )
    },
    { accessorKey: 'payee', header: 'Payee', cell: info => (
        info.row.original.payments.length > 1 
            ? '[multiple]' 
            : info.row.original.payments[0].payee.name
        ) 
    },
    { accessorKey: 'reference', header: 'Reference', cell: info => (
        info.row.original.payments.length > 1 
            ? '[multiple]' 
            : info.row.original.payments[0].reference
        ) 
    },
    { accessorKey: 'totalAmount', header: 'Total', cell: info => {
        const { totalsByCurrency } = getPaymentBatchSummary(info.row.original.payments);
        const sortedTotals = [...totalsByCurrency].sort((a, b) =>
            b.totalAmount - a.totalAmount || a.currency.localeCompare(b.currency)
        );
        return (
            <ul className="m-0 list-none space-y-0.5 p-0 tabular-nums">
                {sortedTotals.map((total) => (
                    <li key={total.currency} className="whitespace-nowrap">
                        {total.currency} {currencyFormat(total.totalAmount)}
                    </li>
                ))}
            </ul>
        );
    }, meta: { className: 'text-left' }},
    { accessorKey: 'status', header: 'Status', cell: info => (
        <PaymentStatus status={info.row.original.status} className="text-xs" />
    )},
    { accessorKey: 'approvalProgress', header: 'Approval Progress', cell: info => (
        <CountProgress
            value={info.row.original.obtainedAuthorisations}
            total={info.row.original.requiredAuthorisations}
        />
    ), meta: { className: 'text-center' }},
    { accessorKey: 'action', header: '', cell: info => {
        const paymentPath = `/payments/${info.row.original.paymentId}`;
        const organisationId = info.row.original._organisationId;
        // In multi-org view, carry the payment's organisation id so the detail
        // page fetches (and authorises) against the correct organisation.
        const to = showOrganisation && organisationId != null
            ? `${paymentPath}?orgId=${organisationId}`
            : paymentPath;
        return (
            <Button
                asChild
                variant="outline"
                size="sm"
                className="w-full gap-1.5 text-xs font-semibold bg-yavrio-green/5 text-yavrio-green border-yavrio-green/30 hover:bg-yavrio-green/10 hover:border-yavrio-green/50 hover:text-yavrio-green-dark"
            >
                <Link
                    to={to}
                    state={{ fromSearchParams: searchParams }}
                >
                    View
                    <ArrowRight className="h-3.5 w-3.5" />
                </Link>
            </Button>
        );
    }, enableHiding: false, meta: { className: 'text-right' }},
];

const COLUMN_VISIBILITY_STORAGE_KEY = 'yvr-payments-table-column-visibility';
const DEFAULT_COLUMN_VISIBILITY = { payer: false, intercompanyTransfer: false, paymentType: false };

const INTERCOMPANY_FILTER_ALL = 'all';
const INTERCOMPANY_FILTER_VALUES = { only: true, exclude: false };

const loadColumnVisibility = () => {
    try {
        const stored = JSON.parse(localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY));
        return { ...DEFAULT_COLUMN_VISIBILITY, ...(stored && typeof stored === 'object' ? stored : {}) };
    } catch {
        return DEFAULT_COLUMN_VISIBILITY;
    }
};

export default function PaymentsTable({ 
  payments, 
  title, 
  loading, 
  tableType, 
  onQueryChange,
  initialStatus = null,
  timezone = null,
  showOrganisation = false,
  organisations = [],
  paymentMethods = []
}) {
  const [searchParams] = useSearchParams();
  const data = payments.data.reverse();
  const meta = payments.meta;
  const [globalFilter, setGlobalFilter] = React.useState('');
  const [columnVisibility, setColumnVisibility] = React.useState(loadColumnVisibility);

  React.useEffect(() => {
    try {
      localStorage.setItem(COLUMN_VISIBILITY_STORAGE_KEY, JSON.stringify(columnVisibility));
    } catch {
      // Storage unavailable (e.g. private mode); the preference just won't persist.
    }
  }, [columnVisibility]);
  
  // Memoize columns with current search params to preserve pagination when navigating
  const columns = React.useMemo(
    () => createColumns(searchParams.toString(), timezone, showOrganisation),
    [searchParams, timezone, showOrganisation]
  ); 
  const [paymentTypeFilter, setPaymentTypeFilter] = React.useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = React.useState(
    initialStatus && initialStatus !== 'Approved,Rejected' ? initialStatus : ''
  );
  const [paymentStatus, setPaymentStatus] = React.useState(
    tableType === 'Pending' ? 'Pending' : (initialStatus || 'Approved,Rejected')
  );
  const [toDate, setToDate] = React.useState(null);
  const [fromDate, setFromDate] = React.useState(null);
  const [searchValue, setSearchValue] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [selectedPaymentTypes, setSelectedPaymentTypes] = React.useState([]);
  const [selectedOrganisationIds, setSelectedOrganisationIds] = React.useState([]);
  const [intercompanyFilter, setIntercompanyFilter] = React.useState(INTERCOMPANY_FILTER_ALL);
  // Track if user has interacted with search to prevent resetting page on mount
  const hasUserSearched = React.useRef(false);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    manualPagination: true,
    pageCount: meta.pageInfo.lastPage || 0,
    onPaginationChange: (updater) => {
        const newPagination =
          typeof updater === "function"
            ? updater(table.getState().pagination)
            : updater;
      },
    state: {
        globalFilter,
        columnVisibility,
        pagination: {
            pageIndex: meta.pageInfo.page - 1 || 0,
            pageSize: meta.pageInfo.limit || 10,
        },
    },
  })

const buildFilters = (overrides = {}) => {
    const filters = {};
    if (selectedPaymentTypes.length > 0) filters.paymentMethod = selectedPaymentTypes.join(',');
    if (fromDate) filters.fromDate = fromDate;
    if (toDate) filters.toDate = toDate;
    if (debouncedSearch) filters.search = debouncedSearch;
    if (intercompanyFilter in INTERCOMPANY_FILTER_VALUES) {
        filters.intercompanyTransfer = INTERCOMPANY_FILTER_VALUES[intercompanyFilter];
    }
    if (showOrganisation && selectedOrganisationIds.length > 0) {
        filters.organisationIds = selectedOrganisationIds;
    }
    return { ...filters, ...overrides };
}

const handleResetFilters = () => {
    hasUserSearched.current = false;
    setPaymentTypeFilter('');
    setPaymentStatusFilter('');
    setPaymentStatus(tableType === 'Pending' ? 'Pending' : 'Approved,Rejected');
    setFromDate(null);
    setToDate(null);
    setSearchValue('');
    setDebouncedSearch('');
    setSelectedPaymentTypes([]);
    setSelectedOrganisationIds([]);
    setIntercompanyFilter(INTERCOMPANY_FILTER_ALL);
    onQueryChange(1, tableType === 'Pending' ? 'Pending' : 'Approved,Rejected', meta.pageInfo.limit, {});
}

const isColumnVisible = (columnId) => table.getColumn(columnId)?.getIsVisible() ?? false;

// A hidden column's filter can't be seen or changed, so drop it rather than leave it silently applied.
const clearFilterForHiddenColumn = (columnId) => {
    let overrides;
    let status = paymentStatus;
    if (columnId === 'status' && paymentStatusFilter) {
        setPaymentStatusFilter('');
        status = 'Approved,Rejected';
        setPaymentStatus(status);
        overrides = {};
    } else if (columnId === 'createdAt' && (fromDate || toDate)) {
        setFromDate(null);
        setToDate(null);
        overrides = { fromDate: undefined, toDate: undefined };
    } else if (columnId === 'paymentMethod' && selectedPaymentTypes.length > 0) {
        setSelectedPaymentTypes([]);
        overrides = { paymentMethod: undefined };
    } else if (columnId === '_organisationName' && selectedOrganisationIds.length > 0) {
        setSelectedOrganisationIds([]);
        overrides = { organisationIds: undefined };
    } else if (columnId === 'intercompanyTransfer' && intercompanyFilter !== INTERCOMPANY_FILTER_ALL) {
        setIntercompanyFilter(INTERCOMPANY_FILTER_ALL);
        overrides = { intercompanyTransfer: undefined };
    }
    if (overrides) {
        onQueryChange(1, status, meta.pageInfo.limit, buildFilters(overrides));
    }
}

React.useEffect(() => {
  const handler = setTimeout(() => {
    setDebouncedSearch(searchValue); // updates after 500ms
  }, 500);

  return () => {
    clearTimeout(handler); // clears previous timeout
  };
}, [searchValue]);

React.useEffect(() => {
  // Only call onQueryChange if user has actually typed in the search box
  if (!hasUserSearched.current) {
    return;
  }
  
  onQueryChange(
    1,
    paymentStatus,
    meta.pageInfo.limit,
    buildFilters({ search: debouncedSearch })
  );
}, [debouncedSearch]);
  

  return (
    <>
    <Card>
        <CardHeader>
            <h3>{title}</h3>
            <div className="flex items-center">
               <div className="flex flex-row flex-wrap gap-4 w-full">
                <div className="flex flex-col grow">
                    <p className="text-sm text-muted-foreground">Search</p>
                    <Input 
                        placeholder="Search by Ref, Payee, Payer, Amount, etc." 
                        value={searchValue} 
                        onChange={(e) => {
                            hasUserSearched.current = true;
                            setSearchValue(e.target.value);
                        }}
                    />
                </div>
                    {tableType === 'Approved' && isColumnVisible('status') && (
                        <div className="flex flex-col grow">
                            <p className="text-sm text-muted-foreground">Status</p>
                            <Select onValueChange={
                                (value) => {
                                    setPaymentStatusFilter(value);
                                    setPaymentStatus(value);
                                    onQueryChange(
                                            1,
                                            value,
                                            meta.pageInfo.limit,
                                            buildFilters()
                                    )
                                }}
                                value={paymentStatusFilter}>
                                <SelectTrigger className="">
                                    <SelectValue placeholder="Select a Status" />
                                </SelectTrigger>
                                <SelectContent style={{ zIndex: 1300 }}>
                                    <SelectItem value="Approved">Approved</SelectItem>
                                    <SelectItem value="Rejected">Rejected</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    {showOrganisation && organisations.length > 0 && isColumnVisible('_organisationName') && (
                        <div className="flex flex-col grow">
                            <p className="text-sm text-muted-foreground">Organisation</p>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        role="combobox"
                                        className="justify-between min-w-[225px]"
                                    >
                                        {selectedOrganisationIds.length > 0
                                            ? selectedOrganisationIds
                                                .map((id) => organisations.find((org) => String(org.id) === String(id))?.name)
                                                .filter(Boolean)
                                                .join(', ')
                                            : 'All organisations'}
                                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[300px] p-0">
                                    <Command>
                                        <CommandInput placeholder="Search organisations..." />
                                        <CommandList>
                                            <CommandEmpty>No organisations found.</CommandEmpty>
                                            <CommandGroup>
                                                {organisations.map((organisation) => {
                                                    const orgId = String(organisation.id);
                                                    const isSelected = selectedOrganisationIds.includes(orgId);
                                                    return (
                                                        <CommandItem
                                                            key={orgId}
                                                            value={organisation.name}
                                                            onSelect={() => {
                                                                const nextSelected = isSelected
                                                                    ? selectedOrganisationIds.filter((id) => id !== orgId)
                                                                    : [...selectedOrganisationIds, orgId];
                                                                setSelectedOrganisationIds(nextSelected);
                                                                onQueryChange(
                                                                    1,
                                                                    paymentStatus,
                                                                    meta.pageInfo.limit,
                                                                    buildFilters({ organisationIds: nextSelected })
                                                                );
                                                            }}
                                                        >
                                                            <Check
                                                                className={`mr-2 h-4 w-4 ${isSelected ? 'opacity-100' : 'opacity-0'}`}
                                                            />
                                                            {organisation.name}
                                                        </CommandItem>
                                                    );
                                                })}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                        </div>
                    )}
                {isColumnVisible('intercompanyTransfer') && (
                <div className="flex flex-col grow">
                    <p className="text-sm text-muted-foreground">Intercompany</p>
                    <Select
                        value={intercompanyFilter}
                        onValueChange={(value) => {
                            setIntercompanyFilter(value);
                            onQueryChange(
                                1,
                                paymentStatus,
                                meta.pageInfo.limit,
                                buildFilters({ intercompanyTransfer: INTERCOMPANY_FILTER_VALUES[value] })
                            );
                        }}
                    >
                        <SelectTrigger>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent style={{ zIndex: 1300 }}>
                            <SelectItem value={INTERCOMPANY_FILTER_ALL}>Any</SelectItem>
                            <SelectItem value="only">Intercompany</SelectItem>
                            <SelectItem value="exclude">Non-intercompany</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                )}
                {isColumnVisible('paymentMethod') && (
                <div className="flex flex-col grow">
                    <p className="text-sm text-muted-foreground">Payment Method</p>
                    <Popover >
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                role="combobox"
                                className="justify-between min-w-[225px]"
                            >
                                {selectedPaymentTypes.length > 0
                                ? selectedPaymentTypes.map(value => paymentMethods.find(method => method.value === value)?.label).join(', ')
                                : "Select payment method..."}
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[300px] p-0">
                            <Command>
                                <CommandInput placeholder="Search payment methods..." />
                                <CommandList>
                                <CommandEmpty>No payment methods found.</CommandEmpty>
                                <CommandGroup>
                                    {paymentMethods.map((paymentMethod) => (
                                    <CommandItem
                                        key={paymentMethod.value}
                                        value={paymentMethod.label}
                                        keywords={paymentMethod.value.split(',')}
                                        onSelect={() => {
                                            
                                             const newSelectedTypes = selectedPaymentTypes.includes(paymentMethod.value)
                                                 ? selectedPaymentTypes.filter(value => value !== paymentMethod.value)
                                                 : [...selectedPaymentTypes, paymentMethod.value];
                                             setSelectedPaymentTypes(newSelectedTypes);
                                             onQueryChange(
                                                 1,
                                                 paymentStatus,
                                                 meta.pageInfo.limit,
                                                 buildFilters({ paymentMethod: newSelectedTypes.join(',') })
                                             );
                                         }}
                                    >
                                        <Check
                                        className={`mr-2 h-4 w-4 ${selectedPaymentTypes.includes(paymentMethod.value) ? "opacity-100" : "opacity-0"}`}
                                        />
                                        {paymentMethod.label}
                                    </CommandItem>
                                    ))}
                                </CommandGroup>
                                </CommandList>
                            </Command>
                            </PopoverContent>
                        </Popover>
                    </div>
                )}
                    {isColumnVisible('createdAt') && (
                    <>
                    <div className="flex flex-col grow">
                        <p className="text-sm text-muted-foreground">From</p>
                        <Popover>
                        <PopoverTrigger asChild>
                            <Button
                            variant="outline"
                            data-empty={!fromDate}
                            className="justify-between text-left font-normal"
                            >
                                {fromDate ? `${format(fromDate, 'dd/MM/yyyy')}` : 'Select a Date'}
                                <CalendarIcon size={16}></CalendarIcon>
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 pr-4">
                            <Calendar mode="single" selected={fromDate} onSelect={(value) => {
                                setFromDate(value);
                                onQueryChange(
                                    1,
                                    paymentStatus,
                                    meta.pageInfo.limit,
                                    buildFilters({ fromDate: format(value, 'yyyy-MM-dd') })
                                )
                            }} defaultMonth={fromDate} />
                        </PopoverContent>
                        </Popover>
                    </div>
                    <div className="flex flex-col grow">
                        <p className="text-sm text-muted-foreground">To</p>
                        <Popover>
                        <PopoverTrigger asChild>
                            <Button
                            variant="outline"
                            data-empty={!toDate}
                            className=" justify-between text-left font-normal"
                            >
                                {toDate ? `${format(toDate, 'dd/MM/yyyy')}` : 'Select a Date'}
                                <CalendarIcon size={16}></CalendarIcon>
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 pr-4">
                            <Calendar mode="single" selected={toDate} onSelect={(value) => {
                                setToDate(value);
                                onQueryChange(
                                    1,
                                    paymentStatus,
                                    meta.pageInfo.limit,
                                    buildFilters({ toDate: format(value, 'yyyy-MM-dd') })
                                )
                            }} defaultMonth={toDate} />
                        </PopoverContent>
                        </Popover>
                    </div>
                    </>
                    )}
                    <div className="flex flex-row gap-2 pt-7">
                    <Button className="gap-2" variant="outline" onClick={handleResetFilters}>
                     <RotateCcw size={16}></RotateCcw>Reset Filters
                    </Button>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button className="gap-2" variant="outline">
                                <Columns3 size={16} />Columns
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuLabel>Visible columns</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            {table.getAllLeafColumns()
                                .filter(column => column.getCanHide())
                                .map(column => (
                                    <DropdownMenuCheckboxItem
                                        key={column.id}
                                        checked={column.getIsVisible()}
                                        onCheckedChange={(value) => {
                                            column.toggleVisibility(!!value);
                                            if (!value) clearFilterForHiddenColumn(column.id);
                                        }}
                                        onSelect={(e) => e.preventDefault()}
                                    >
                                        {column.columnDef.header}
                                    </DropdownMenuCheckboxItem>
                                ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                    </div>
                </div>
            </div>
        </CardHeader>
        <CardContent>
        <div className="w-full">
            <div className="">
                <AsyncContent
                    loading={loading}
                    skeleton={<TableRowsSkeleton rows={8} cols={table.getVisibleLeafColumns().length} />}
                >
                    <Table className="text-xs [&_th]:px-3 [&_th]:h-9 [&_th]:text-xs [&_td]:px-3 [&_td]:py-1.5">
                    <TableHeader>
                        {table.getHeaderGroups().map(headerGroup => (
                        <TableRow key={headerGroup.id} className>
                            {headerGroup.headers.map(header => {
                            return (
                                <TableHead key={header.id} className={header.column.columnDef.meta?.className}>
                                {header.isPlaceholder
                                    ? null
                                    : flexRender(
                                        header.column.columnDef.header,
                                        header.getContext()
                                    )}
                                </TableHead>
                            )
                            })}
                        </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                        table.getRowModel().rows.map(row => (
                            <TableRow
                            className={(row.original.status === 'Approved' || row.original.status === 'Rejected') && 'text-gray-500'}
                            key={row.id}
                            >
                            {row.getVisibleCells().map(cell => (
                                <TableCell key={cell.id} className={cell.column.columnDef.meta?.className}>
                                {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext()
                                )}
                                </TableCell>
                            ))}
                            </TableRow>
                        ))
                        ) : (
                        <TableRow>
                            <TableCell
                            colSpan={table.getVisibleLeafColumns().length}
                            className="h-16 text-center"
                            >
                            No results.
                            </TableCell>
                        </TableRow>
                        )}
                    </TableBody>
                    </Table>
                </AsyncContent>
            </div>
            <div className="flex items-center justify-end space-x-2 py-4">
                <div className="text-sm text-muted-foreground mr-6">
                    Page {meta.pageInfo.page} of {meta.pageInfo.lastPage}
                </div>
                <div className="space-x-2">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                        onQueryChange(
                          meta.pageInfo.page - 1,
                          paymentStatus, 
                          meta.pageInfo.limit,
                          buildFilters()
                        )
                      }
                      disabled={meta.pageInfo.page === 1}
                >
                    Previous
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                        onQueryChange(
                          meta.pageInfo.page + 1,
                          paymentStatus, 
                          meta.pageInfo.limit,
                          buildFilters()
                        )
                      }
                      disabled={!meta.pageInfo.hasNextPage}
                >
                    Next
                </Button>
                </div>
            </div>
            </div>
        </CardContent>
    </Card>
    </>
  )
}
